import {
  createAnalyticsClient,
  type AnalyticsConsent,
  type MarketingPageContext,
} from "@rental-game/analytics";
import { ANALYTICS_CONSENT_EVENT } from "./consent";

declare const __POSTHOG_KEY__: string;
declare const __POSTHOG_HOST__: string;

const ALLOWED_CAMPAIGNS = {
  utm_source: new Set(["youtube", "instagram", "facebook", "linkedin", "newsletter", "community"]),
  utm_medium: new Set(["organic_social", "social", "email", "referral", "video"]),
  utm_campaign: new Set(["waitlist_launch", "north_florida_launch", "product_launch"]),
} as const;

export function sanitizedPageParameters(location: Location): MarketingPageContext {
  const query = new URLSearchParams(location.search);
  const result: MarketingPageContext = {
    surface: "marketing",
    page_origin: location.origin,
    page_path: location.pathname,
  };
  const source = query.get("utm_source")?.toLowerCase();
  const medium = query.get("utm_medium")?.toLowerCase();
  const campaign = query.get("utm_campaign")?.toLowerCase();
  if (source && ALLOWED_CAMPAIGNS.utm_source.has(source)) result.campaign_source = source;
  if (medium && ALLOWED_CAMPAIGNS.utm_medium.has(medium)) result.campaign_medium = medium;
  if (campaign && ALLOWED_CAMPAIGNS.utm_campaign.has(campaign)) result.campaign_name = campaign;
  return result;
}

export function initializeAnalytics(document: Document, view: Window): () => void {
  const client = createAnalyticsClient({
    token: typeof __POSTHOG_KEY__ === "string" ? __POSTHOG_KEY__ : undefined,
    host: typeof __POSTHOG_HOST__ === "string" ? __POSTHOG_HOST__ : undefined,
    document,
    view,
  });
  let active = false;
  let disposed = false;
  let lastState: AnalyticsConsent | undefined;

  const pageParameters = (): MarketingPageContext => ({
    ...sanitizedPageParameters(view.location),
    page_title: document.title,
  });
  const capturePageView = (): void => {
    if (!active || disposed) return;
    void client.capture("marketing_page_view", pageParameters());
  };
  const updateConsent = async (state: AnalyticsConsent): Promise<void> => {
    if (disposed || state === lastState) return;
    lastState = state;
    if (state === "granted") {
      await client.setConsent("granted");
      if (disposed || lastState !== "granted") return;
      active = true;
      capturePageView();
      return;
    }
    active = false;
    await client.setConsent(state);
  };
  const onConsent = (event: Event): void => {
    const state = (event as CustomEvent<{ state?: AnalyticsConsent }>).detail?.state ?? "unknown";
    void updateConsent(state);
  };
  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (active && target?.closest("[data-analytics-waitlist-cta]"))
      void client.capture("waitlist_cta_clicked", pageParameters());
    if (active && target?.closest("[data-analytics-product-signup-cta]"))
      void client.capture("product_signup_cta_clicked", pageParameters());
  };
  const onSuccess = (): void => {
    if (active)
      void client.capture("waitlist_submission_succeeded", {
        surface: "marketing",
        page_path: view.location.pathname,
      });
  };
  view.addEventListener(ANALYTICS_CONSENT_EVENT, onConsent);
  document.addEventListener("click", onClick);
  view.addEventListener("marketing:waitlist-success", onSuccess);
  void updateConsent(
    document.documentElement.dataset.analyticsConsent === "granted" ? "granted" : "unknown",
  );

  return () => {
    disposed = true;
    active = false;
    view.removeEventListener(ANALYTICS_CONSENT_EVENT, onConsent);
    document.removeEventListener("click", onClick);
    view.removeEventListener("marketing:waitlist-success", onSuccess);
    client.dispose();
  };
}
